import { useEffect, useRef } from 'react';

import { Color, colors } from 'theme';
import { getStaticImageSrc } from 'utils/getImageSrc';
import { useLoadSvgContent } from 'utils/hooks';

import { IconContainer } from './Icon.styled';
import { IconName } from './IconName';
import { cssFilterForColor } from './cssFilterForColor';

export type IconProps = {
  name: IconName;
  size: number;
  initialColorName?: Color;
  keepColorOnLoad?: boolean;
  opacity?: number;
  'data-qa-id'?: string;
};

export function Icon({
  name,
  size,
  initialColorName = 'selectionGrey',
  keepColorOnLoad,
  opacity,
  'data-qa-id': dataQaId,
}: IconProps) {
  const { svgSrc, svgContent, loadSvgContent } = useLoadSvgContent(
    getStaticImageSrc(`icons/normalized/${name}.svg`),
  );

  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    if (img.complete) loadSvgContent();
    else img.addEventListener('load', loadSvgContent);
  }, [loadSvgContent]);

  const color = keepColorOnLoad ? colors[initialColorName] : undefined;

  if (!svgContent) {
    return (
      <IconContainer
        $size={size}
        $color={color}
        $opacity={opacity}
        data-qa-id={dataQaId}
      >
        <img
          ref={imgRef}
          src={svgSrc}
          width={size}
          height={size}
          alt={`${name} icon`}
          style={{ filter: cssFilterForColor(initialColorName) }}
        />
      </IconContainer>
    );
  }

  return (
    <IconContainer
      $size={size}
      $color={color}
      $opacity={opacity}
      data-qa-id={dataQaId}
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  );
}
